// src/pages/public/Careers/CareersPage.jsx
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FaMapMarkerAlt, FaClock, FaArrowRight } from 'react-icons/fa'

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import { fetchJobs } from '../../../redux/slices/jobsSlice.js'

import SEO from '../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import SkeletonCard from '../../../components/common/SkeletonCard.jsx'
import LoadMoreButton from '../../../components/common/LoadMoreButton.jsx'

import './CareersPage.scss'

export default function CareersPage() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { currentPage: careersPage, loading: pageLoading } = useAppSelector(
        (state) => state.cms
    )
    const { jobs, featuredJobs, loading: jobsLoading, hasMore } = useAppSelector(
        (state) => state.jobs
    )

    useEffect(() => {
        dispatch(fetchPageBySlug('careers'))
        dispatch(fetchJobs({ isOpen: true, page: 1, limit: 10 }))
    }, [dispatch])

    useEffect(() => {
        if (jobs.length == 0) {
            dispatch(fetchJobs({ isOpen: true, page: 1, limit: 10 }))
        }
    }, [])

    const getSectionData = (sectionKey) => {
        return careersPage?.sections?.find((section) => section.key === sectionKey)?.data
    }

    const heroSection = getSectionData('hero')
    const featuredJobsSection = getSectionData('featuredJobs')
    const allOpenJobsSection = getSectionData('allOpenJobs')
    const agencySnapsSection = getSectionData('ourAgencySnaps')

    const loadMoreJobs = () => {
        const nextPage = Math.ceil(jobs.length / 10) + 1
        dispatch(fetchJobs({ isOpen: true, page: nextPage, limit: 10 }))
    }

    if (pageLoading && !careersPage) {
        return (
            <>
                <SEO title="Careers" />
                <div className="space-y-20">
                    {/* Skeleton with simple layout – fine to keep */}
                    <section className="bg-gray-50">
                        <div className="container-custom section-padding">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-pulse">
                                <div>
                                    <div className="h-12 bg-black-200 rounded w-1/2 mb-6"></div>
                                    <div className="h-6 bg-black-200 rounded w-3/4 mb-8"></div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="h-48 bg-black-200 rounded-lg"></div>
                                    <div className="h-48 bg-black-200 rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="bg-white">
                        <div className="container-custom section-padding">
                            <div className="h-8 bg-black-200 rounded w-1/3 mb-12 animate-pulse"></div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {[...Array(3)].map((_, i) => (
                                    <SkeletonCard key={i} />
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </>
        )
    }

    return (
        <>
            <SEO
                title={careersPage?.seoTitle || 'Careers'}
                description={careersPage?.seoDescription}
            />

            {/* Whole page is dark like v1 */}
            <div className="careers-page-root">
                {/* HERO SECTION – v1 layout */}
                <section className="premium-careers-hero">
                    <div className="careers-hero-container">
                        <div className="hero-grid">
                            {/* Left column: heading + subtitle + small image */}
                            <motion.div
                                className="left-column"
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            >
                                <motion.div
                                    className="text-content"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.6 }}
                                >
                                    <motion.h1
                                        className="hero-heading"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.8, delay: 0.8 }}
                                    >
                                        {heroSection?.heading || 'Build Your Career With Us'}
                                    </motion.h1>

                                    <motion.p
                                        className="hero-subtitle"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.8, delay: 1 }}
                                    >
                                        {heroSection?.subheading ||
                                            'Join a team that ships serious products and solves real problems.'}
                                    </motion.p>
                                </motion.div>

                                <motion.div
                                    className="secondary-image-container"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 1.2 }}
                                >
                                    {heroSection?.images?.[0] && (
                                        <LazyImage
                                            src={heroSection.images[0]}
                                            alt="Work environment"
                                            className="secondary-hero-image"
                                            placeholder={
                                                <div className="secondary-hero-image skeleton-bg"></div>
                                            }
                                        />
                                    )}
                                </motion.div>
                            </motion.div>

                            {/* Right column: big hero image */}
                            <motion.div
                                className="right-column"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                            >
                                {heroSection?.images?.[1] && (
                                    <motion.div
                                        initial={{ scale: 0.9 }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: 0.8, delay: 0.8 }}
                                    >
                                        <LazyImage
                                            src={heroSection.images[1]}
                                            alt="Team collaboration"
                                            className="main-hero-image"
                                            placeholder={
                                                <div className="main-hero-image skeleton-bg"></div>
                                            }
                                        />
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* FEATURED JOBS – v1 FeaturedJobs layout */}
                <section className="opportunities-section">
                    <div className="opportunities-container">
                        <div className="opportunities-header">
                            <motion.h2
                                className="section-heading"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                {(featuredJobsSection?.heading ||
                                    'Featured opportunities'.toUpperCase())}
                            </motion.h2>

                            <motion.div
                                className="section-description"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <p>
                                    {featuredJobsSection?.subheading ||
                                        'A few roles where we’re especially eager to meet you.'}
                                </p>
                            </motion.div>
                        </div>

                        {jobsLoading && featuredJobs.length === 0 ? (
                            <div className="opportunities-grid">
                                {[...Array(3)].map((_, i) => (
                                    <SkeletonCard key={i} />
                                ))}
                            </div>
                        ) : featuredJobs.length > 0 ? (
                            <div className="opportunities-grid">
                                {featuredJobs.slice(0, 3).map((job, index) => {
                                    const title = job.type || 'Featured role'
                                    const heading = job.title
                                    const subheading =
                                        job.shortDescription ||
                                        job.description ||
                                        'Join our team and make an impact.'
                                    const location = job.location || 'Location not specified'
                                    const time = job.type || 'Full-time'

                                    return (
                                        <motion.div
                                            key={job._id}
                                            className="opportunity-card"
                                            initial={{ opacity: 0, y: 30 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.6, delay: index * 0.1 }}
                                            whileHover={{
                                                y: -5,
                                                transition: { duration: 0.2 },
                                            }}
                                            onClick={() => navigate(`/careers/${job.slug}`)}
                                        >
                                            <div className="card-header">
                                                <span className="card-title">{title}</span>
                                                <button className="card-arrow">
                                                    <FaArrowRight />
                                                </button>
                                            </div>

                                            <div className="card-content">
                                                <h3 className="card-heading">{heading}</h3>
                                                <p className="card-subheading">{subheading}</p>

                                                <div className="card-details">
                                                    <span className="detail-item">
                                                        <FaMapMarkerAlt className="detail-icon" />
                                                        {location}
                                                    </span>
                                                    <span className="detail-item">
                                                        <FaClock className="detail-icon" />
                                                        {time}
                                                    </span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )
                                })}
                            </div>
                        ) : (
                            <EmptyState
                                title="No Featured Positions"
                                message="Check back later for featured job opportunities."
                            />
                        )}
                    </div>
                </section>

                {/* ALL OPEN JOBS – v1 vertical section layout */}
                <AllOpenJobsSection
                    allOpenJobsSection={allOpenJobsSection}
                    jobs={jobs}
                    jobsLoading={jobsLoading}
                    hasMore={hasMore}
                    onLoadMore={loadMoreJobs}
                    navigate={navigate}
                />

                {/* AGENCY SNAPS – v1 mosaic layout */}
                {agencySnapsSection && (
                    <AgencySnapsMosaic agencySnapsSection={agencySnapsSection.agencySnapsSection} />
                )}
            </div>
        </>
    )
}

/**
 * All Open Jobs section – v1 vertical layout (desktop) + card grid (mobile)
 */
function AllOpenJobsSection({
    allOpenJobsSection,
    jobs,
    jobsLoading,
    hasMore,
    onLoadMore,
    navigate,
}) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 1000 : false
    )

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 1000
            setIsMobile(mobile)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const heading = allOpenJobsSection?.heading || 'Open Positions'

    if (jobsLoading && jobs.length === 0) {
        return (
            <section className="vertical-opportunities-section">
                <div className="vertical-opportunities-container">
                    <h2 className="section-heading">{heading}</h2>
                    <div className="opportunities-grid">
                        {[...Array(6)].map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    if (!jobs.length) {
        return (
            <section className="vertical-opportunities-section">
                <div className="vertical-opportunities-container">
                    <h2 className="section-heading">{heading}</h2>
                    <EmptyState
                        title="No Open Positions"
                        message="We're not currently hiring, but check back soon for new opportunities."
                        action="Join Talent Network"
                        onAction={() => (window.location.href = '/contact')}
                    />
                </div>
            </section>
        )
    }

    return (
        <section className="vertical-opportunities-section">
            <div className="vertical-opportunities-container">
                <motion.h2
                    className="section-heading"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    {heading}
                </motion.h2>

                {!isMobile ? (
                    <div className="vertical-opportunities-list">
                        {jobs.map((job, index) => {
                            const title = job.type || 'Role'
                            const heading = job.title
                            const subheading =
                                job.shortDescription ||
                                job.description ||
                                'Join our team and make an impact.'
                            const location = job.location || 'Location not specified'
                            const time = job.type || 'Full-time'

                            return (
                                <motion.div
                                    key={job._id}
                                    className="vertical-opportunity-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.05 }}
                                    whileHover={{
                                        backgroundColor: '#111',
                                        borderColor: 'var(--primary)',
                                    }}
                                    onClick={() => navigate(`/careers/${job.slug}`)}
                                >
                                    <div className="col col-1">
                                        <span className="card-title">{title}</span>
                                        <h3 className="card-heading">{heading}</h3>
                                    </div>

                                    <div className="col col-2">
                                        <p className="card-subheading">{subheading}</p>
                                        <div className="card-details">
                                            <span className="detail-item">
                                                <FaMapMarkerAlt className="detail-icon" />
                                                {location}
                                            </span>
                                            <span className="detail-item">
                                                <FaClock className="detail-icon" />
                                                {time}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col col-3">
                                        <button className="card-arrow">
                                            <FaArrowRight />
                                        </button>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="opportunities-grid">
                        {jobs.map((job, index) => {
                            const title = job.type || 'Role'
                            const heading = job.title
                            const subheading =
                                job.shortDescription ||
                                job.description ||
                                'Join our team and make an impact.'
                            const location = job.location || 'Location not specified'
                            const time = job.type || 'Full-time'

                            return (
                                <motion.div
                                    key={job._id}
                                    className="opportunity-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                    onClick={() => navigate(`/careers/${job.slug}`)}
                                >
                                    <div className="card-header">
                                        <span className="card-title">{title}</span>
                                        <button className="card-arrow">
                                            <FaArrowRight />
                                        </button>
                                    </div>

                                    <div className="card-content">
                                        <h3 className="card-heading">{heading}</h3>
                                        <p className="card-subheading">{subheading}</p>

                                        <div className="card-details">
                                            <span className="detail-item">
                                                <FaMapMarkerAlt className="detail-icon" />
                                                {location}
                                            </span>
                                            <span className="detail-item">
                                                <FaClock className="detail-icon" />
                                                {time}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>
                )}

                <div className="load-more-container">
                    <LoadMoreButton
                        hasMore={hasMore}
                        loading={jobsLoading}
                        onLoadMore={onLoadMore}
                    />
                </div>
            </div>
        </section>
    )
}

/**
 * Agency Snaps – v1 ImageMosaic layout, using agencySnapsSection.images
 */
function AgencySnapsMosaic({ agencySnapsSection }) {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    })

    const images = agencySnapsSection || []

    const groupImagesIntoRows = (imgs) => {
        const rows = []
        for (let i = 0; i < imgs.length; i += 2) {
            const rowImages = imgs.slice(i, i + 2)
            rows.push(rowImages)
        }
        return rows
    }

    const imageRows = groupImagesIntoRows(images)

    return (
        <section className="image-mosaic-section" ref={ref}>
            <div className="mosaic-container">
                <div className="mosaic-header">
                    <motion.h2
                        className="mosaic-heading"
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {agencySnapsSection.heading?.toUpperCase() || 'OUR WORKPLACE CULTURE'}
                    </motion.h2>

                    <motion.p
                        className="mosaic-subheading"
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        {agencySnapsSection.subheading ||
                            'Discover what makes our team special and how we collaborate to create amazing products.'}
                    </motion.p>
                </div>

                {images.length === 0 && (
                    <div className="no-images-message">
                        <p>No images available</p>
                    </div>
                )}

                {images.length > 0 && (
                    <div className="masonry-grid">
                        {imageRows.map((row, rowIndex) => (
                            <div
                                key={rowIndex}
                                className={`masonry-row ${row.length === 1 ? 'single-image' : 'double-image'
                                    } ${rowIndex % 2 === 0 ? 'row-even' : 'row-odd'}`}
                            >
                                {row.map((imageUrl, imageIndex) => (
                                    <MasonryImage
                                        key={`${rowIndex}-${imageIndex}`}
                                        src={imageUrl}
                                        alt={`Office culture ${rowIndex * 2 + imageIndex + 1}`}
                                        isSingleImage={row.length === 1}
                                        inView={inView}
                                        totalIndex={rowIndex * 2 + imageIndex}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}

function MasonryImage({ src, alt, isSingleImage, inView, totalIndex }) {
    return (
        <motion.div
            className={`masonry-item ${isSingleImage ? 'single' : 'double'}`}
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
            transition={{
                duration: 0.6,
                delay: 0.6 + totalIndex * 0.1,
                ease: 'easeOut',
            }}
            whileHover={{
                scale: 1.02,
                transition: { duration: 0.3 },
            }}
        >
            <div className="masonry-image-container">
                <img src={src} alt={alt} />
                <div className="image-overlay"></div>
            </div>
        </motion.div>
    )
}
